import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { UseOrNot } from "../../../assets/data/Constants";
import { TkRadioGroup } from "../TkRadioGroup";
import TkLabelText from "../TkLabelText";
import TkButton from "../TkButton";
import { ITkTransferCodeTextProps } from "../../../type/componentsType/TkTransferCodeText";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";
import "./_style.scss";
import { TkFormContext } from "../../../context/tkFormContext";

/**
 * TkTransferCodeTextコンポーネントは、転送コードのテキスト入力フィールドと関連するラジオボタンを表示します。
 *
 * @param {ITkTransferCodeTextProps} props - TkTransferCodeTextコンポーネントのプロパティ
 * @param {Function} props.onChange - 入力フィールドの値が変更されたときに実行されるコールバック関数
 * @param {Function} props.onSuccess - 保存ボタンがクリックされたときに実行されるコールバック関数
 * @param {boolean} [props.disabled=false] - 入力フィールドと保存ボタンを無効にするかどうかを示すフラグ
 * @param {string} [props.errorMessage] - エラーメッセージのテキスト
 * @param {string} [props.wantToSetValue="0"] - ラジオボタンの初期値
 * @returns {React.FC<ITkTransferCodeTextProps>} TkTransferCodeTextコンポーネント
 */
export const TkTransferCodeText: React.FC<ITkTransferCodeTextProps> = (
  props
) => {
  const {
    onChange,
    onSuccess,
    onGetValue,
    disabled = false,
    wantToSetValue,
  } = props;

  const [showText, setShowText] = useState<boolean>(false);
  const [radioValue, setRadioValue] = useState<string>(UseOrNot["利用しない"]);
  const [inputValue, setInputValue] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const { formContext, setFormContext } = useContext(TkFormContext);

  useEffect(() => {
    if (wantToSetValue) {
      setShowText(true);
      setInputValue(wantToSetValue);
      setRadioValue(UseOrNot["利用する"]);
      onChange && onChange(wantToSetValue);
    }

    if (disabled) {
      setShowText(true);
      setRadioValue(UseOrNot["利用する"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantToSetValue, disabled]);

  const radioList = Object.entries(UseOrNot).map(([label, value]) => ({
    label,
    value,
  }));

  /**
   *　選択されたラジオの値を取得するためのコールバック関数です。
   *
   * @param {string} value - 選択されたラジオの値
   */
  const handleGetValue = (event: any) => {
    setShowText(event?.target?.value === UseOrNot["利用する"]);

    setInputValue("");
    setErrorMsg("");
    onChange && onChange("");
    onGetValue && onGetValue(event?.target?.value);
  };

  /**
   * 入力フィールドの値が変化したときに処理するコールバック関数です。
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - 入力フィールドのchangeイベントオブジェクト
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange && onChange(e.target.value);
  };

  useEffect(() => {
    if (!inputValue.match(/^([a-zA-Z0-9]{6})?$/)) {
      setErrorMsg(TkPropertyMessageGet("TKMSG_SCR0077E", "6"));
      setFormContext({ preventSubmit: { transferCodeText: true } });
    } else {
      setErrorMsg("");
      if (formContext?.preventSubmit?.transferCodeText)
        setFormContext({ preventSubmit: { transferCodeText: false } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  /**
   * 保存ボタンがクリックされたときに処理するコールバック関数で、入力フィールドの値を検証し、エラーメッセージがある場合はonSuccessコールバック関数を実行しません。
   */
  const handleOnSave = () => {
    if (!inputValue) {
      setErrorMsg(TkPropertyMessageGet("TKMSG_SCR0043E"));
      return;
    }

    if (errorMsg) {
      return;
    }

    onChange && onChange(inputValue);
    onSuccess && onSuccess(inputValue);
  };

  return (
    <Box>
      <Box className="tkTransferCodeBox">
        <TkRadioGroup
          wantToSetValue={radioValue}
          onChange={handleGetValue}
          disabled={disabled}
          radioPropsList={radioList}
        />

        {showText && (
          <Box className="codeReader">
            <TkLabelText
              error={!!errorMsg}
              helperText={errorMsg}
              onChange={handleChange}
              wantToSetValue={inputValue}
              inputProps={{ maxLength: 6 }}
            />
            <TkButton data-testid="TkTransferRead" onClick={handleOnSave}>
              読み込み
            </TkButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TkTransferCodeText;
